import type { PagesConfig, PagesConsentManagementConfig } from '@shared/types/PagesConfig';
import { useContextStore } from '@/Stores/context';
import { useConfigStore } from '@/Stores/config';

export const SUPPORTED_CMP_PROVIDERS = ['consentmanager.de2', 'cookiebot'] as const;

export type SupportedCmpProvider = (typeof SUPPORTED_CMP_PROVIDERS)[number];

export interface ConsentContextFlags {
  isKite: boolean;
  isWcIntegration: boolean;
}

export interface ActiveConsentManagement {
  provider: SupportedCmpProvider;
  id: string;
}

function normalizeProvider(value: string | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function isSupportedCmpProvider(value: string): value is SupportedCmpProvider {
  return (SUPPORTED_CMP_PROVIDERS as readonly string[]).includes(value);
}

function shouldBypassConsent(context: ConsentContextFlags): boolean {
  return context.isKite || context.isWcIntegration;
}

/*
  consentmanager's own autoblocking already replaces/restores blocked iframes and shows its
  own consent prompt for them.
*/
const PROVIDERS_WITH_NATIVE_CONTENT_BLOCKING: readonly SupportedCmpProvider[] = [
  'consentmanager.de2',
];

function usesNativeContentBlocking(provider: SupportedCmpProvider): boolean {
  return PROVIDERS_WITH_NATIVE_CONTENT_BLOCKING.includes(provider);
}

function getConsentManagementConfig(
  config: PagesConfig | null | undefined,
): PagesConsentManagementConfig | undefined {
  return config?.widget_settings?.consent_management as PagesConsentManagementConfig | undefined;
}

export function isConsentManagementConfigured(config: PagesConfig | null | undefined): boolean {
  const consentManagement = getConsentManagementConfig(config);

  return consentManagement?.enabled === true && String(consentManagement.id || '').trim() !== '';
}

export function getActiveConsentManagement(
  config: PagesConfig | null | undefined,
): ActiveConsentManagement | null {
  const consentManagement = getConsentManagementConfig(config);

  if (consentManagement?.enabled !== true) {
    return null;
  }

  const provider = normalizeProvider(consentManagement.provider);
  const id = String(consentManagement.id || '').trim();

  if (!id || !isSupportedCmpProvider(provider)) {
    return null;
  }

  return {
    provider,
    id,
  };
}

export function isConsentManagementActive(): boolean {
  const contextStore = useContextStore();
  const configStore = useConfigStore();
  const config = configStore && typeof configStore !== 'string' ? configStore.config : null;

  const context: ConsentContextFlags = {
    isKite: contextStore.isKite,
    isWcIntegration: contextStore.isWcIntegration,
  };

  if (shouldBypassConsent(context)) {
    return false;
  }

  const activeConsentManagement = getActiveConsentManagement(config);
  if (!activeConsentManagement) {
    return false;
  }

  return !usesNativeContentBlocking(activeConsentManagement.provider);
}
